import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { demoActivities, yearOf } from "./utils/activities";
const Context = createContext(null);
export async function request(action, options = {}) {
  const controller = new AbortController();
  const cancel = () => controller.abort();
  options.signal?.addEventListener("abort", cancel, { once: true });
  if (options.signal?.aborted) controller.abort();
  const timer = setTimeout(cancel, 20000);
  let response;
  try {
    response = await fetch(`/api/strava?action=${action}`, {
      credentials: "same-origin",
      ...options,
      signal: controller.signal,
    });
    const data = await response.json().catch(() => ({
      message: "The server returned an unexpected response. Please try again.",
    }));
    if (!response.ok)
      throw Object.assign(new Error(data.message), {
        code: data.error || "invalid_response",
        retryAfter: data.retryAfter,
      });
    return data;
  } catch (error) {
    if (options.signal?.aborted || error.code) throw error;
    throw Object.assign(
      new Error(
        "The connection timed out or could not reach Strava. Please try again.",
      ),
      { code: "network_error" },
    );
  } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener("abort", cancel);
  }
}
export function AppProvider({ children }) {
  const [profile, setProfile] = useState(null),
    [demo, setDemo] = useState(false),
    [ready, setReady] = useState(false),
    [sessionError, setSessionError] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear()),
    [units, setUnitsState] = useState(() => {
      try {
        return localStorage.getItem("sw_units") === "mi" ? "mi" : "km";
      } catch {
        return "km";
      }
    });
  const cache = useRef({});
  const sessionVersion = useRef(0);
  async function restore() {
    const version = ++sessionVersion.current;
    setReady(false);
    setSessionError(null);
    try {
      const data = await request("session");
      if (version === sessionVersion.current) setProfile(data.athlete);
    } catch (e) {
      if (version === sessionVersion.current) setSessionError(e);
    } finally {
      if (version === sessionVersion.current) setReady(true);
    }
  }
  useEffect(() => {
    restore();
  }, []);
  const startDemo = () => {
    sessionVersion.current++;
    cache.current = {};
    setDemo(true);
    setProfile({ firstname: "Ankit", id: "demo" });
    setReady(true);
    setSessionError(null);
  };
  const signOut = async () => {
    if (!demo) await request("logout", { method: "POST" });
    sessionVersion.current++;
    cache.current = {};
    setDemo(false);
    setProfile(null);
  };
  const setUnits = (value) => {
    setUnitsState(value);
    try {
      localStorage.setItem("sw_units", value);
    } catch {}
  };
  return (
    <Context.Provider
      value={{
        profile,
        demo,
        ready,
        sessionError,
        restore,
        startDemo,
        signOut,
        year,
        setYear,
        units,
        setUnits,
        cache,
      }}
    >
      {children}
    </Context.Provider>
  );
}
export const useApp = () => useContext(Context);
export function useActivities() {
  const { profile, demo, year, cache } = useApp();
  const [data, setData] = useState([]),
    [loading, setLoading] = useState(false),
    [error, setError] = useState(null),
    [progress, setProgress] = useState(0),
    [revision, setRevision] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    async function load() {
      setData([]);
      setError(null);
      setProgress(0);
      if (!profile) return;
      if (demo) {
        setData(demoActivities(year));
        setLoading(false);
        return;
      }
      if (cache.current[year]) {
        setData(cache.current[year]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        let all = [];
        for (let page = 1; page <= 30; page++) {
          const result = await request(`activities&year=${year}&page=${page}`, {
            signal: controller.signal,
          });
          if (controller.signal.aborted) return;
          all = all.concat(result.activities);
          setProgress(all.length);
          if (!result.hasMore) {
            const unique = [
              ...new Map(all.map((a) => [a.id, a])).values(),
            ].filter((a) => yearOf(a) === year);
            cache.current[year] = unique;
            setData(unique);
            return;
          }
        }
        throw new Error(
          "This year has more than 6,000 activities. The import stopped to protect Strava’s request limit. Please contact the site owner.",
        );
      } catch (e) {
        if (!controller.signal.aborted) setError(e);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }
    load();
    return () => controller.abort();
  }, [profile, demo, year, cache, revision]);
  return {
    data,
    loading,
    error,
    progress,
    retry: () => {
      delete cache.current[year];
      setRevision((x) => x + 1);
    },
  };
}
